import _ from 'lodash'
import Snap from './snap'
import * as GuideLine from './guides/line'
import * as HiddenGuide from './guides/hidden'

const typeFilter = {
  tl: ['left', 'top', 'horizontal-middle', 'vertical-middle'],
  tc: ['top', 'horizontal-middle'],
  tr: ['right', 'top', 'horizontal-middle', 'vertical-middle'],
  br: ['bottom', 'right', 'horizontal-middle', 'vertical-middle'],
  bc: ['bottom', 'horizontal-middle'],
  bl: ['left', 'bottom', 'horizontal-middle', 'vertical-middle'],
  lm: ['left', 'vertical-middle'],
  rm: ['right', 'vertical-middle']
}

/*
 * A snap type that snaps based on wob position. Matches coords of wobs
 * so if wobA right side is close to another wobB right side, it will snap so the right
 * sides of the wobs are aligned.
 */
const ElementSnap = Snap.extend({

  getGuides: function (area, previousArea, handleType) {
    let guides = []
      , areas = this.areas(area)

    areas.forEach(area => area.coords = this.coords(area))

    var props = handleType ? typeFilter[handleType] : Object.keys(areas[0].coords) // be sure to collect the keys before we shift below to ensure there will be an object
      , current = areas.shift()

    areas.forEach(area => {
      Object.keys(current.coords) // Check each of the coords
            .filter(prop => props.indexOf(prop) !== -1) // Unless they are resizing. We filter coord types depending on the resize handle
            .sort((a, b) => this.coordSort[a] - this.coordSort[b])
            .forEach(typeA => {
              let coordA = current.coords[typeA]
                , coordB = area.coords[typeA]

              if (handleType && typeA == this.dominantProp) {
                // If they are resizing, ignore the dominantProp (middle coord)
                return
              }

              // First check if these two coords align.
              if (this.withinThreshold(coordA - coordB)) {
                // Make sure we don't already have a guide that is set to the dominant property between these two objects #222
                let dominated = guides.find(guide => {
                  return guide.prop === this.dominantProp && guide.to == area
                })

                if (!dominated) {
                  guides.push(new this.guideType({direction: this.direction, from: current, to: area, prop: typeA, canvas: this.canvas, diff: coordB - coordA}))
                }
              } else {
                // Now check if these two shapes are touching based on their `adjacentProps`. See #242
                let typeB = this.adjacentProps[typeA]
                  , coordC = area[typeB]

                // If we have an adjacentProp to check, these wobs intersect on the same plane, and we're within the snap threshold
                if (typeB && this.intersect(current, area) && this.withinThreshold(coordA - coordC)) {
                  guides.push(new this.hiddenGuideType({direction: this.direction, from: current, to: area, prop: typeB, canvas: this.canvas, diff: coordC - coordA }))
                }
              }
            })
    })

    // Return the guides, filtering to make sure we don't have duplicate guides
    return this.removeOverlapping(guides)
  },

  /*
   * Removes overlapping line segments on the incoming guides. Uses the `props`
   * to determine the coordinates needed to determine if line segments overlap.
   */
  removeOverlapping: function (props, guides) {
    let visible = guides.filter(g => g.coords) // Guide lines that are visible in the DOM
      , invisible = guides.filter(g => !g.coords) // Not visible guide lines
      , groups = _.groupBy(visible, `coords.${props.groupBy}`)

    Object.keys(groups).forEach(key => {
      if (groups[key].length > 1) {
        let min = _.minBy(groups[key], `coords.${props.minProp}`).coords[props.minProp]
          , max = _.maxBy(groups[key], `coords.${props.maxProp}`).coords[props.maxProp]

        groups[key].length = 1 // Just use the first guide

        // Swap its coords to use the single continuous line
        groups[key][0].coords[props.minProp] = min
        groups[key][0].coords[props.dimension] = max - min
      }
    })
    return [].concat(...(_.values(groups))).concat(invisible)
  }

})

const HorizontalElementSnap = ElementSnap.extend({

  direction: 'left',

  guideType: GuideLine.Vertical,

  hiddenGuideType: HiddenGuide.Vertical,

  dominantProp: 'vertical-middle',

  /*
   * Defines the order in which we should check the coords. Needed to ensure
   * we check the middle property first, so we can escape future comparisons. See #222
   */
  coordSort: {
    'vertical-middle': 0,
    'left': 1,
    'right': 2
  },

  adjacentProps: {
    left: 'right', // Check if a left matches some other object's right
    right: 'left' // Check if a right property matches another shape's left.
  },

  removeOverlapping: ElementSnap.prototype.removeOverlapping.bind(null, {
    groupBy: 'left',
    minProp: 'top',
    maxProp: 'bottom',
    dimension: 'height'
  }),

  intersect: ElementSnap.prototype.horizontalIntersect,

  /*
   * Returns the coords we should check for alignment of this snap type
   */
  coords: function (area) {
    return {
      'vertical-middle': area.left + area.width / 2,
      'left': area.left,
      'right': area.left + area.width
    }
  }
})

const VerticalElementSnap = ElementSnap.extend({

  direction: 'top',

  guideType: GuideLine.Horizontal,

  hiddenGuideType: HiddenGuide.Horizontal,

  dominantProp: 'horizontal-middle',

  /*
  * Defines the order in which we should check the coords. Needed to ensure
  * we check the middle property first, so we can escape future comparisons. See #222
  */
  coordSort: {
    'horizontal-middle': 0,
    'top': 1,
    'bottom': 2
  },

  adjacentProps: {
    top: 'bottom', // Check if a top matches some other object's bottom
    bottom: 'top' // Also check if a bottom coord matches some other shape's top.
  },

  removeOverlapping: ElementSnap.prototype.removeOverlapping.bind(null, {
    groupBy: 'top',
    minProp: 'left',
    maxProp: 'right',
    dimension: 'width'
  }),

  intersect: ElementSnap.prototype.verticalIntersect,

  /*
   * Returns the coords we should check for alignment of this snap type
   */
  coords: function (area) {
    return {
      'horizontal-middle': area.top + area.height / 2,
      'top': area.top,
      'bottom': area.top + area.height
    }
  }
})

export const Vertical = VerticalElementSnap
export const Horizontal = HorizontalElementSnap
